<template>
  <button
    type="button"
    :class="
      $b({
        active: isActive,
        shape,
        limitedEdition: isLimitedEdition,
        fullWidth,
      })
    "
    @click.stop="$emit('select')"
  >
    <span :class="$b('outer')">
      <span :class="$b('inner')">
        <span
          :class="$b('colour')"
          :style="styles"
        >
          <img
            v-if="image"
            :class="$b('image')"
            :src="image"
          />

          <span
            v-if="$slots.default"
            :class="$b('label')"
          >
            <slot />
          </span>

          <span
            v-if="$slots.icon"
            :class="$b('icon')"
          >
            <slot name="icon" />
          </span>
        </span>
      </span>
    </span>
  </button>
</template>

<script>
export default {
  name: 'BaseChoice',
  props: {
    styles: { type: String, required: true },
    isActive: { type: Boolean, required: true },
    isLimitedEdition: { type: Boolean, default: false },
    image: { type: String, default: '' },
    recycled: { type: Boolean, default: false },
    shape: {
      type: String,
      default: 'square',
      validator: (value) => {
        return ['square', 'circle'].includes(value)
      },
      required: false,
    },
    fullWidth: { type: Boolean, default: false },
  },
  emits: ['select'],
}
</script>

<style lang="postcss">
.BaseChoice {
  position: relative;
  display: block;
  appearance: none;
  font-size: 0;
  background-color: transparent;
  border-radius: 15%;
  border: none;
  padding: 4px;
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &:hover:not(.disabled):not(:disabled) {
    background-color: transparent;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &--shape-circle {
    border-radius: 100%;
    padding: 4px 8px 4px 0;
  }

  &--full-width {
    @apply w-full h-full block p-0 absolute top-0 left-0;
  }
}

.BaseChoice__outer {
  position: relative;
  display: block;
  width: 40px;
  height: 40px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 15%;
    border: 1px solid theme('colors.off-black');
    opacity: 0;
    transition: opacity 0.2s ease;

    .BaseChoice:hover &,
    .BaseChoice--active & {
      opacity: 1;
    }
  }

  .BaseChoice--shape-circle & {
    width: 40px;
    height: 40px;

    &::before {
      border-radius: 50%;
    }
  }

  .BaseChoice--limited-edition & {
    &::after {
      @apply text-white leading-none
        rounded-full absolute
        flex items-center justify-center
        border-white border-solid border-[2px];
      content: 'LE';
      background-image: var(--gradient-default);
      font-size: 7px;
      width: 19px;
      height: 19px;
      top: 2px;
      right: -6px;
      padding-left: 1px;
    }
  }

  .BaseChoice--full-width & {
    @apply w-full h-full;
  }
}

.BaseChoice__inner {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 15%;
  transition: transform 0.2s ease;
  transform: scale(calc(40 / 40));

  .BaseChoice--shape-circle & {
    border-radius: 50%;
  }

  .BaseChoice:hover &,
  .BaseChoice--active & {
    transform: scale(calc(32 / 40));
  }
}

.BaseChoice__colour {
  width: 100%;
  flex-grow: 1;
  color: theme('colors.white');
}

.BaseChoice__label {
  @apply absolute text-center
    uppercase;
  top: 50%;
  left: 50%;
  font-size: 7px;
  transform: translate(-50%, -50%);
}

.BaseChoice__icon {
  @apply absolute
    leading-none
    text-white;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
}

.BaseChoice__image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: left;
}

.BaseChoice__recycled-overlay {
  opacity: 0.8;
  mix-blend-mode: screen;
}
</style>
